/*
 * Copyright (c) 2026 Oray Inc. All rights reserved.
 *
 * No Part of this file may be reproduced, stored
 * in a retrieval system, or transmitted, in any form, or by any means,
 * electronic, mechanical, photocopying, recording, or otherwise,
 * without the prior consent of Oray Inc.
 *
 * @Author: huangweiliang<huangweiliang@oray.com>
 * @Date: 2026-01-21
 * @LastEditors: huangweiliang<huangweiliang@oray.com>
 * @LastEditTime: 2026-01-21 18:17:27
 * @FileName: pc.ts
 * @Description: []
 */
import { ClientStatus, WebStatus, LoginStrategy, DecisionResult, FastLoginOptions } from '../types'
import { Account } from '../../../types'
import { parseClientParam } from '../parser'

/**
 * 核心决策引擎 (PC端)
 * @param clientStatus 客户端登录状态（通常由本地服务检测）
 * @param webStatus Web端登录状态（由 Cookie/Session 检测）
 * @param options 配置项
 */
export function decidePCLoginStrategy(clientStatus: ClientStatus, webStatus: WebStatus, options: FastLoginOptions = {}): DecisionResult {
  // 1. 获取 URL 参数中的指定账号信息 (Client Url Param)
  // 优先使用传入的 options.specifiedAccountId, 否则尝试从 URL 解析
  const queryString =
    options.urlParams && typeof URLSearchParams !== 'undefined'
      ? new URLSearchParams(options.urlParams).toString()
      : typeof window !== 'undefined'
        ? window.location.search
        : ''

  const clientParam = parseClientParam(queryString)

  // 默认有效期 30 分钟
  const validityDuration = options.timestampValidityDuration ?? 30 * 60
  let isExpired = false

  if (clientParam && validityDuration > 0) {
    const now = Math.floor(Date.now() / 1000)
    // 校验: 当前时间 - 参数时间 > 有效期
    if (now - clientParam.t > validityDuration) {
      isExpired = true
      console.warn(
        `[OrayFastLoginSDK] _cl param expired. Timestamp: ${clientParam.t}, Now: ${now}, Diff: ${now - clientParam.t}, Max: ${validityDuration}`,
      )
    }
  }

  // 如果参数存在但过期了，我们应当忽略它，即视作没有指定账号。
  // 用户可能希望明确知道是因为过期了，所以这里可以由开发者决定是降级为普通流程，还是返回特定状态。
  // 按照通常逻辑，“失效”意味着回到没有该参数时的状态（但保留客户端登录状态的检测）。
  // 这里我们将 specifiedAccountId 置空，并在日志中记录。
  const specifiedAccountId =
    !isExpired && (options.specifiedAccountId || clientParam?.i) ? options.specifiedAccountId || clientParam?.i : undefined

  // 获取 UA 中的应用名称部分（转小写）
  const getUAAppName = (ua: string) => {
    if (!ua) return ''
    const firstPart = ua.split('/')[0]
    return firstPart.toLowerCase()
  }

  const clientUAAppName = clientParam?.u ? getUAAppName(clientParam.u) : ''

  // 历史兼容：将旧版客户端上报的 source 映射为标准应用名 (UA App Name)
  const LEGACY_SOURCE_MAP: Record<string, string> = {
    client_pgy: 'pgyvisitorent',
  }

  // 节点 A -> B: 检测客户端是否登录
  if (!clientStatus.isLoggedIn || clientStatus.accounts.length === 0) {
    // B -> 否 -> D
    return { strategy: LoginStrategy.NORMAL_LOGIN }
  }

  // B -> 是 -> C: 快捷登录方式

  // 节点 C -> E: 是否有指定登录账号？
  if (specifiedAccountId) {
    // E -> 是

    // 节点 F: Web 是否已登录？
    if (webStatus.isLoggedIn && webStatus.currentUser) {
      // 查找客户端中对应的指定账号信息
      const specifiedClientAccount = clientStatus.accounts.find((acc) => {
        const isUserMatch = acc.userid === specifiedAccountId

        const accSourceLower = acc.source?.toLowerCase()
        const normalizedSource = accSourceLower ? LEGACY_SOURCE_MAP[accSourceLower] || accSourceLower : accSourceLower

        const isClientMatch = clientUAAppName ? normalizedSource === clientUAAppName : true
        return isUserMatch && isClientMatch
      })

      // F -> 已登录 -> H: Web 登录账号与客户端账号是否一致？
      if (webStatus.currentUser.userid === specifiedAccountId) {
        // H -> 一致 -> J
        return {
          strategy: LoginStrategy.MAINTAIN_WEB_LOGIN,
          data: {
            webAccount: webStatus.currentUser,
            clientAccount: specifiedClientAccount,
            specifiedParam: clientParam || undefined,
          },
        }
      } else {
        // H -> 不一致 -> K
        return {
          strategy: LoginStrategy.SHOW_CONFLICT_LOGIN,
          data: {
            webAccount: webStatus.currentUser,
            clientAccount: specifiedClientAccount,
            specifiedParam: clientParam || undefined,
          },
        }
      }
    } else {
      // F -> 未登录 -> I: 展示指定账号快捷登录页面
      const specifiedClientAccount = clientStatus.accounts.find((acc) => {
        const isUserMatch = acc.userid === specifiedAccountId

        const accSourceLower = acc.source?.toLowerCase()
        const normalizedSource = accSourceLower ? LEGACY_SOURCE_MAP[accSourceLower] || accSourceLower : accSourceLower

        const isClientMatch = clientUAAppName ? normalizedSource === clientUAAppName : true
        return isUserMatch && isClientMatch
      })

      return {
        strategy: LoginStrategy.SHOW_SPECIFIED_LOGIN,
        data: {
          webAccount: webStatus.currentUser,
          clientAccount: specifiedClientAccount,
          specifiedParam: clientParam || undefined,
        },
      }
    }
  } else {
    // E -> 否 (没有指定账号) -> G: 是否多个端登录中？
    if (clientStatus.accounts.length > 1) {
      // G -> 是 -> L
      return {
        strategy: LoginStrategy.SHOW_ACCOUNT_SELECTION,
        data: {
          webAccount: webStatus.currentUser,
          clientAccounts: clientStatus.accounts,
        },
      }
    } else {
      // G -> 否 -> M
      return {
        strategy: LoginStrategy.SHOW_SINGLE_FAST_LOGIN,
        data: {
          clientAccount: clientStatus.accounts[0],
        },
      }
    }
  }
}
